/**
 * Workforce Intelligence Index (WII) System
 * 
 * Core intelligence layer for the Zodiac Workforce Intelligence Institute
 * Provides scoring, profiling, and personalized recommendations based on the 7 Forces of Workforce Intelligence
 * 
 * System Hierarchy:
 * - Individual WII → Personal workforce intelligence measurement
 * - Organizational WII → Company-level aggregation (future)
 * - Institutional WII → Sector/industry benchmarking (future)
 */

// ============================================
// SEVEN FORCES OF WORKFORCE INTELLIGENCE
// ============================================

export const SEVEN_FORCES = {
  CAPABILITY_ACCELERATION: {
    id: "capability_acceleration",
    name: "Capability Acceleration",
    shortName: "Capability",
    description: "The ability to rapidly develop, upgrade, and apply skills in response to changing workforce demands and technological evolution.",
    color: "#E97451",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["business", "career"],
    relatedToolCategories: ["coding-development", "automation-workflow"]
  },
  WORKFORCE_AUTONOMY: {
    id: "workforce_autonomy",
    name: "Workforce Autonomy",
    shortName: "Autonomy",
    description: "The capacity of individuals to operate with independence, decision-making ability, and adaptive responsibility within dynamic environments.",
    color: "#4CAF50",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["service", "business"],
    relatedToolCategories: ["project-management", "automation-workflow"]
  },
  TRUST_ALIGNMENT: {
    id: "trust_alignment",
    name: "Trust & Alignment",
    shortName: "Trust",
    description: "The degree to which individuals, teams, and systems are aligned in purpose, values, and execution, supported by mutual trust.",
    color: "#2196F3",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["service", "career"],
    relatedToolCategories: ["communication", "project-management"]
  },
  LEARNING_TRANSFORMATION: {
    id: "learning_transformation",
    name: "Learning Transformation",
    shortName: "Learning",
    description: "The shift from traditional learning to continuous, adaptive, and intelligence-driven development integrated into real-world application.",
    color: "#1E3A8A",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["career", "service"],
    relatedToolCategories: ["research-learning", "content-writing"]
  },
  OPPORTUNITY_FLUIDITY: {
    id: "opportunity_fluidity",
    name: "Opportunity Fluidity",
    shortName: "Opportunity",
    description: "The ability to access, recognize, and convert opportunities into economic, professional, or organizational value.",
    color: "#9C27B0",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["business", "service"],
    relatedToolCategories: ["data-analytics", "research-learning"]
  },
  QUALITY_DISCIPLINE: {
    id: "quality_discipline",
    name: "Quality Discipline",
    shortName: "Quality",
    description: "The consistency of standards, execution excellence, and adherence to systems that ensure reliability and performance.",
    color: "#F44336",
    weight: 0.10,
    assessmentQuestions: 3,
    relatedOpportunities: ["career", "business"],
    relatedToolCategories: ["automation-workflow", "project-management"]
  },
  INSTITUTIONAL_COHERENCE: {
    id: "institutional_coherence",
    name: "Institutional Coherence",
    shortName: "Institution",
    description: "The alignment between policies, structures, and systems that enable sustainable workforce development at scale.",
    color: "#607D8B",
    weight: 0.15,
    assessmentQuestions: 3,
    relatedOpportunities: ["career"],
    relatedToolCategories: ["data-analytics", "communication"]
  }
};

export const SEVEN_FORCES_ARRAY = Object.values(SEVEN_FORCES);

// ============================================
// WII CLASSIFICATION LEVELS
// ============================================

export const WII_LEVELS = {
  LOW: {
    id: "low",
    name: "Low Readiness",
    label: "Developing Foundation",
    range: [0, 40],
    color: "#F44336",
    description: "Focus on foundational skill development and learning pathways",
    recommendedFocus: "development",
    opportunityTypes: ["career"], // Start with career pathways
    toolComplexity: "basic"
  },
  MODERATE: {
    id: "moderate",
    name: "Moderate Readiness",
    label: "Building Capability",
    range: [41, 70],
    color: "#FF9800",
    description: "Mixed pathway combining development with opportunity exploration",
    recommendedFocus: "mixed",
    opportunityTypes: ["career", "service"],
    toolComplexity: "intermediate"
  },
  HIGH: {
    id: "high",
    name: "High Readiness",
    label: "Advanced Intelligence",
    range: [71, 100],
    color: "#4CAF50",
    description: "Ready for advanced opportunities and leadership pathways",
    recommendedFocus: "opportunity",
    opportunityTypes: ["business", "service", "career"],
    toolComplexity: "advanced"
  }
};

// ============================================
// USER PROFILE STRUCTURE
// ============================================

/**
 * Default user profile template
 * Used to initialize new users
 */
export const DEFAULT_USER_PROFILE = {
  // Profile metadata
  id: null,
  createdAt: null,
  updatedAt: null,
  assessmentCompleted: false,
  assessmentDate: null,
  
  // Seven Forces Scores (0-100 each)
  forceScores: {
    capability_acceleration: null,
    workforce_autonomy: null,
    trust_alignment: null,
    learning_transformation: null,
    opportunity_fluidity: null,
    quality_discipline: null,
    institutional_coherence: null
  },
  
  // Calculated metrics
  wiiScore: null,
  wiiLevel: null, // low, moderate, high
  
  // Analysis
  strengths: [], // Top 2-3 forces
  gaps: [], // Lowest 2-3 forces
  
  // Readiness classification
  readinessLevel: null, // basic, developing, advanced
  
  // User preferences (for future personalization)
  preferences: {
    industryFocus: null,
    opportunityInterest: [], // business, service, career
    experienceLevel: null, // entry, mid, senior
    learningStyle: null
  },
  
  // Recommendations (generated)
  recommendations: {
    opportunities: [],
    toolStacks: [],
    developmentPathway: []
  }
};

// ============================================
// WII CALCULATION FUNCTIONS
// ============================================

/**
 * Calculate the Workforce Intelligence Index (WII) from force scores
 * V1 Formula: Weighted average of 7 forces, normalized to 0-100
 * 
 * @param {Object} forceScores - Object with force IDs as keys and scores (0-100) as values
 * @returns {number} WII score (0-100)
 */
export function calculateWII(forceScores) {
  if (!forceScores) return null;
  
  let totalWeight = 0;
  let weightedSum = 0;
  
  SEVEN_FORCES_ARRAY.forEach(force => {
    const score = forceScores[force.id];
    if (score !== null && score !== undefined) {
      weightedSum += score * force.weight;
      totalWeight += force.weight;
    }
  });
  
  if (totalWeight === 0) return null;
  
  // Normalize to account for any missing scores
  const normalizedScore = (weightedSum / totalWeight) * (1 / 0.15) * 0.15;
  
  // Ensure score is within 0-100 range
  return Math.min(100, Math.max(0, Math.round(normalizedScore)));
}

/**
 * Classify WII score into readiness level
 * @param {number} wiiScore 
 * @returns {Object} WII level object
 */
export function classifyWII(wiiScore) {
  if (wiiScore === null || wiiScore === undefined) return null;
  
  if (wiiScore <= WII_LEVELS.LOW.range[1]) {
    return WII_LEVELS.LOW;
  } else if (wiiScore <= WII_LEVELS.MODERATE.range[1]) {
    return WII_LEVELS.MODERATE;
  } else {
    return WII_LEVELS.HIGH;
  }
}

/**
 * Identify top strengths from force scores
 * @param {Object} forceScores 
 * @param {number} count - Number of strengths to return (default 3)
 * @returns {Array} Top force objects with scores
 */
export function identifyStrengths(forceScores, count = 3) {
  if (!forceScores) return [];
  
  const scoredForces = SEVEN_FORCES_ARRAY
    .filter(force => forceScores[force.id] !== null && forceScores[force.id] !== undefined)
    .map(force => ({
      ...force,
      score: forceScores[force.id]
    }))
    .sort((a, b) => b.score - a.score);
  
  return scoredForces.slice(0, count);
}

/**
 * Identify gaps (lowest scores) from force scores
 * @param {Object} forceScores 
 * @param {number} count - Number of gaps to return (default 3)
 * @returns {Array} Lowest force objects with scores
 */
export function identifyGaps(forceScores, count = 3) {
  if (!forceScores) return [];
  
  const scoredForces = SEVEN_FORCES_ARRAY
    .filter(force => forceScores[force.id] !== null && forceScores[force.id] !== undefined)
    .map(force => ({
      ...force,
      score: forceScores[force.id]
    }))
    .sort((a, b) => a.score - b.score);
  
  return scoredForces.slice(0, count);
}

/**
 * Determine readiness level from force scores
 * @param {Object} forceScores 
 * @returns {string} 'basic', 'developing', or 'advanced'
 */
export function determineReadinessLevel(forceScores) {
  const wii = calculateWII(forceScores);
  if (wii === null) return null;
  
  const level = classifyWII(wii);
  if (level.id === 'low') return 'basic';
  if (level.id === 'moderate') return 'developing';
  return 'advanced';
}

// ============================================
// PROFILE GENERATION
// ============================================

/**
 * Generate a complete user profile from force scores
 * @param {Object} forceScores 
 * @param {Object} preferences - Optional user preferences
 * @returns {Object} Complete user profile
 */
export function generateUserProfile(forceScores, preferences = {}) {
  const wiiScore = calculateWII(forceScores);
  const wiiLevel = classifyWII(wiiScore);
  const strengths = identifyStrengths(forceScores);
  const gaps = identifyGaps(forceScores);
  const readinessLevel = determineReadinessLevel(forceScores);
  
  return {
    ...DEFAULT_USER_PROFILE,
    id: `user_${Date.now()}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    assessmentCompleted: true,
    assessmentDate: new Date().toISOString(),
    forceScores,
    wiiScore,
    wiiLevel: wiiLevel?.id || null,
    strengths: strengths.map(s => s.id),
    gaps: gaps.map(g => g.id),
    readinessLevel,
    preferences: {
      ...DEFAULT_USER_PROFILE.preferences,
      ...preferences
    }
  };
}

// ============================================
// PERSONALIZED MATCHING LOGIC
// ============================================

/**
 * Decision Engine V1 - Rule-based opportunity matching
 * 
 * Logic:
 * - WII < 40: Focus on development (career pathways)
 * - WII 40-70: Mixed pathway (career + service)
 * - WII > 70: Advanced opportunities (all types)
 * 
 * Gap-based recommendations:
 * - Lowest force → priority development area
 * - Map to tools, pathways, opportunities
 */
export const DECISION_ENGINE = {
  /**
   * Get opportunity filter based on WII score
   */
  getOpportunityFilter: (wiiScore) => {
    const level = classifyWII(wiiScore);
    if (!level) return { types: ['career', 'service', 'business'], maxDifficulty: 'advanced' };
    
    return {
      types: level.opportunityTypes,
      maxDifficulty: level.toolComplexity,
      priorityFocus: level.recommendedFocus
    };
  },
  
  /**
   * Get tool complexity filter based on readiness level
   */
  getToolComplexityFilter: (readinessLevel) => {
    switch (readinessLevel) {
      case 'basic':
        return { complexity: ['low'], readinessLevel: ['basic'] };
      case 'developing':
        return { complexity: ['low', 'medium'], readinessLevel: ['basic', 'intermediate'] };
      case 'advanced':
        return { complexity: ['low', 'medium', 'high'], readinessLevel: ['basic', 'intermediate', 'advanced'] };
      default:
        return { complexity: ['low', 'medium'], readinessLevel: ['basic', 'intermediate'] };
    }
  },
  
  /**
   * Map gaps to development priorities
   */
  mapGapsToDevelopment: (gaps) => {
    if (!gaps || gaps.length === 0) return [];
    
    return gaps.map(gapId => {
      const force = SEVEN_FORCES_ARRAY.find(f => f.id === gapId);
      if (!force) return null;
      
      return {
        forceId: gapId,
        forceName: force.name,
        priority: 'high',
        recommendedToolCategories: force.relatedToolCategories,
        recommendedOpportunityTypes: force.relatedOpportunities,
        developmentFocus: `Strengthen ${force.shortName} through targeted practice and tool usage`
      };
    }).filter(Boolean);
  },
  
  /**
   * Map strengths to opportunity types
   */
  mapStrengthsToOpportunities: (strengths) => {
    if (!strengths || strengths.length === 0) return [];
    
    const opportunityMap = {
      capability_acceleration: ['business', 'career'],
      workforce_autonomy: ['service', 'business'],
      trust_alignment: ['service', 'career'],
      learning_transformation: ['career', 'service'],
      opportunity_fluidity: ['business', 'service'],
      quality_discipline: ['career', 'business'],
      institutional_coherence: ['career']
    };
    
    const recommended = new Set();
    strengths.forEach(strengthId => {
      const opportunities = opportunityMap[strengthId] || [];
      opportunities.forEach(opp => recommended.add(opp));
    });
    
    return Array.from(recommended);
  }
};

// ============================================
// OPPORTUNITY SCORING
// ============================================

/**
 * Score an opportunity based on user profile
 * @param {Object} opportunity 
 * @param {Object} userProfile 
 * @returns {Object} Scored opportunity with match details
 */
export function scoreOpportunityForUser(opportunity, userProfile) {
  if (!userProfile || !userProfile.forceScores || !opportunity) {
    return { score: 50, matchDetails: { overall: 'No profile data' } };
  }
  
  let score = 50; // Base score
  const matchDetails = {};
  
  const { wiiScore, wiiLevel, strengths, gaps, readinessLevel } = userProfile;
  
  // 1. WII Level Match (30 points max)
  const oppFilter = DECISION_ENGINE.getOpportunityFilter(wiiScore);
  if (oppFilter.types.includes(opportunity.category)) {
    score += 15;
    matchDetails.categoryMatch = 'Opportunity type matches your readiness level';
  }
  
  // Check difficulty match
  const difficultyMatch = {
    'beginner': { basic: 30, developing: 15, advanced: 5 },
    'intermediate': { basic: 10, developing: 30, advanced: 15 },
    'advanced': { basic: 5, developing: 15, advanced: 30 }
  };
  
  const oppDifficulty = opportunity.difficultyLevel || 'intermediate';
  score += difficultyMatch[oppDifficulty]?.[readinessLevel] || 10;
  matchDetails.difficultyMatch = `${oppDifficulty} difficulty for ${readinessLevel} readiness`;
  
  // 2. Strength Alignment (20 points max)
  if (strengths && opportunity.waitScoreRequirements) {
    const strengthForces = strengths.map(s => {
      if (s === 'capability_acceleration') return 'minAiCapability';
      if (s === 'trust_alignment') return 'minTrust';
      if (s === 'workforce_autonomy' || s === 'learning_transformation') return 'minAlignment';
      return null;
    }).filter(Boolean);
    
    const hasStrengthMatch = strengthForces.some(sf => 
      opportunity.waitScoreRequirements[sf] !== undefined
    );
    
    if (hasStrengthMatch) {
      score += 20;
      matchDetails.strengthMatch = 'Aligns with your strengths';
    }
  }
  
  // 3. Gap Development Value (15 points max)
  if (gaps && gaps.length > 0) {
    const gapForce = SEVEN_FORCES_ARRAY.find(f => f.id === gaps[0]);
    if (gapForce) {
      const hasGapDevelopment = gapForce.relatedOpportunities.includes(opportunity.category);
      if (hasGapDevelopment) {
        score += 15;
        matchDetails.gapDevelopment = `Helps develop ${gapForce.shortName}`;
      }
    }
  }
  
  // 4. Market Demand Bonus (5 points max)
  if (opportunity.marketDemand === 'very high' || opportunity.growthOutlook === 'very high') {
    score += 5;
    matchDetails.demandBonus = 'High market demand';
  }
  
  // Normalize to 0-100
  score = Math.min(100, Math.max(0, score));
  
  return {
    score: Math.round(score),
    matchDetails,
    recommendation: score >= 75 ? 'Highly Recommended' : score >= 50 ? 'Good Fit' : 'Consider After Development'
  };
}

// ============================================
// TOOL RECOMMENDATION
// ============================================

/**
 * Score a tool based on user profile
 * @param {Object} tool 
 * @param {Object} userProfile 
 * @returns {Object} Scored tool with match details
 */
export function scoreToolForUser(tool, userProfile) {
  if (!userProfile || !tool) {
    return { score: 50, matchReason: 'General recommendation' };
  }
  
  let score = 50;
  let matchReason = '';
  
  const { readinessLevel, gaps } = userProfile;
  
  // 1. Readiness Level Match
  const toolComplexity = tool.scores?.integrationComplexity || 'medium';
  const toolReadiness = tool.scores?.aiReadinessLevel || 'intermediate';
  
  const complexityFilter = DECISION_ENGINE.getToolComplexityFilter(readinessLevel);
  
  if (complexityFilter.complexity.includes(toolComplexity)) {
    score += 20;
  }
  if (complexityFilter.readinessLevel.includes(toolReadiness)) {
    score += 15;
    matchReason = `${readinessLevel === 'basic' ? 'Beginner-friendly' : readinessLevel === 'developing' ? 'Good for building skills' : 'Advanced capability'}`;
  }
  
  // 2. Gap Development Match
  if (gaps && gaps.length > 0) {
    const gapForce = SEVEN_FORCES_ARRAY.find(f => f.id === gaps[0]);
    if (gapForce && gapForce.relatedToolCategories.includes(tool.category)) {
      score += 25;
      matchReason = `Helps develop ${gapForce.shortName}`;
    }
  }
  
  // 3. Reliability Bonus
  if (tool.scores?.reliability >= 90) {
    score += 10;
    if (!matchReason) matchReason = 'High reliability';
  }
  
  // 4. Workforce Fit
  if (tool.scores?.workforceFit >= 90) {
    score += 10;
    if (!matchReason) matchReason = 'Excellent workforce fit';
  }
  
  return {
    score: Math.min(100, Math.max(0, Math.round(score))),
    matchReason: matchReason || 'General recommendation'
  };
}

// ============================================
// DEVELOPMENT PATHWAY GENERATION
// ============================================

/**
 * Generate a personalized development pathway based on user profile
 * @param {Object} userProfile 
 * @returns {Array} Development steps
 */
export function generateDevelopmentPathway(userProfile) {
  if (!userProfile) return [];
  
  const { wiiScore, wiiLevel, gaps, readinessLevel } = userProfile;
  const pathway = [];
  
  // Step 1: Foundation based on readiness
  if (readinessLevel === 'basic') {
    pathway.push({
      step: 1,
      phase: "Foundation",
      title: "Build Core Intelligence",
      description: "Establish fundamental workforce intelligence skills through structured learning and basic AI tool adoption.",
      focusAreas: ["AI Literacy", "Basic Tool Proficiency", "Self-Assessment"],
      estimatedDuration: "4-6 weeks",
      milestones: ["Complete AI basics training", "Master 3 essential tools", "Identify strengths"]
    });
  } else if (readinessLevel === 'developing') {
    pathway.push({
      step: 1,
      phase: "Acceleration",
      title: "Accelerate Capability",
      description: "Build on existing foundation with intermediate skills and expanded tool usage.",
      focusAreas: ["Intermediate AI Integration", "Workflow Automation", "Skill Specialization"],
      estimatedDuration: "3-4 weeks",
      milestones: ["Implement 2 automations", "Complete specialization track", "Build portfolio"]
    });
  } else {
    pathway.push({
      step: 1,
      phase: "Optimization",
      title: "Optimize Performance",
      description: "Fine-tune existing capabilities and prepare for leadership or business opportunities.",
      focusAreas: ["Advanced AI Strategy", "Leadership Development", "Opportunity Creation"],
      estimatedDuration: "2-3 weeks",
      milestones: ["Design AI strategy", "Mentor others", "Launch initiative"]
    });
  }
  
  // Step 2: Gap Development (if gaps exist)
  if (gaps && gaps.length > 0) {
    const priorityGap = SEVEN_FORCES_ARRAY.find(f => f.id === gaps[0]);
    if (priorityGap) {
      pathway.push({
        step: 2,
        phase: "Gap Development",
        title: `Strengthen ${priorityGap.shortName}`,
        description: priorityGap.description,
        focusAreas: priorityGap.relatedToolCategories.map(cat => cat.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')),
        estimatedDuration: "3-4 weeks",
        milestones: [`Improve ${priorityGap.shortName} score by 15+`, "Complete related training", "Apply in real context"]
      });
    }
  }
  
  // Step 3: Opportunity Preparation
  const opportunityFocus = DECISION_ENGINE.getOpportunityFilter(wiiScore);
  pathway.push({
    step: pathway.length + 1,
    phase: "Opportunity",
    title: "Prepare for Opportunities",
    description: `Focus on ${opportunityFocus.types.join(', ')} opportunities based on your intelligence profile.`,
    focusAreas: opportunityFocus.types.map(t => t.charAt(0).toUpperCase() + t.slice(1) + " Pathway"),
    estimatedDuration: "4-6 weeks",
    milestones: ["Select target opportunity", "Build required skills", "Create action plan"]
  });
  
  // Step 4: Launch & Growth
  pathway.push({
    step: pathway.length + 1,
    phase: "Launch",
    title: "Launch & Continuous Growth",
    description: "Execute your opportunity plan and establish continuous intelligence development habits.",
    focusAreas: ["Execution", "Feedback Integration", "Continuous Learning"],
    estimatedDuration: "Ongoing",
    milestones: ["Launch first initiative", "Achieve initial results", "Establish growth routine"]
  });
  
  return pathway;
}

// ============================================
// SAMPLE/DEMO PROFILES
// ============================================

export const DEMO_PROFILES = {
  beginner: {
    forceScores: {
      capability_acceleration: 30,
      workforce_autonomy: 35,
      trust_alignment: 45,
      learning_transformation: 32,
      opportunity_fluidity: 28,
      quality_discipline: 38,
      institutional_coherence: 35
    }
  },
  developing: {
    forceScores: {
      capability_acceleration: 62,
      workforce_autonomy: 58,
      trust_alignment: 70,
      learning_transformation: 55,
      opportunity_fluidity: 65,
      quality_discipline: 60,
      institutional_coherence: 52
    }
  },
  advanced: {
    forceScores: {
      capability_acceleration: 85,
      workforce_autonomy: 78,
      trust_alignment: 88,
      learning_transformation: 82,
      opportunity_fluidity: 90,
      quality_discipline: 75,
      institutional_coherence: 80
    }
  }
};

// ============================================
// EXPORTS SUMMARY
// ============================================

export default {
  SEVEN_FORCES,
  SEVEN_FORCES_ARRAY,
  WII_LEVELS,
  DEFAULT_USER_PROFILE,
  calculateWII,
  classifyWII,
  identifyStrengths,
  identifyGaps,
  determineReadinessLevel,
  generateUserProfile,
  DECISION_ENGINE,
  scoreOpportunityForUser,
  scoreToolForUser,
  generateDevelopmentPathway,
  DEMO_PROFILES
};
